import { ApiExternalDtoMeasurementParameterExternalDto } from '@redux/apiSlices/measurementParameterApi';
import { ApiDatabaseDtOsSubstationsDeviceHierarchyDto } from '@redux/apiSlices/substationApi';

export type TreeListItemTsoPqProps = {
  id: string;
  entityId: number;
  type: string;
  label: string;
  description?: string;
  enableAdd?: boolean;
  buttonAction?: () => void;
  options?: TreeListItemTsoPqProps[];
  subcategoryParameters?: number[];
};

//  Without category:

function parseSubstationDevicesNode(node: ApiDatabaseDtOsSubstationsDeviceHierarchyDto): TreeListItemTsoPqProps {
  const { id, entityId, type, label, options } = node;
  const result: TreeListItemTsoPqProps = {
    id: `${id}`,
    entityId: Number(entityId),
    type: `${type}`,
    label: `${label}`,
  };

  if (options && options.length > 0) result.options = options.map((option) => parseSubstationDevicesNode(option));

  return result;
}

export function parseSubstationDevices(
  nodes: ApiDatabaseDtOsSubstationsDeviceHierarchyDto[]
): TreeListItemTsoPqProps[] {
  return nodes.map(parseSubstationDevicesNode);
}

function buildTreeFromCategoryMap<T>(
  categoryMap: Map<string, Map<string, T[]>>,
  t: (value: string) => string,
  mapSubcategory: (params: T[], categoryKey: string, subKey: string, id: number) => TreeListItemTsoPqProps
): TreeListItemTsoPqProps[] {
  const result: TreeListItemTsoPqProps[] = [];

  for (const [categoryKey, subcategoryMap] of categoryMap) {
    const categoryItem: TreeListItemTsoPqProps = {
      id: `category-${categoryKey}`,
      entityId: 0,
      type: 'category',
      label: t(categoryKey),
      options: [],
    };

    for (const [subKeyOriginal, params] of subcategoryMap) {
      const categoryLastPart = categoryKey.split('.').at(-1) ?? '';
      let childKey = subKeyOriginal;

      if (subKeyOriginal.includes(categoryLastPart + '.')) {
        childKey = subKeyOriginal.replace(`${categoryLastPart}.`, '');
      }

      const subItem = mapSubcategory(params, categoryKey, childKey);
      categoryItem.options!.push(subItem);
    }

    result.push(categoryItem);
  }

  return result;
}

function groupParametersByCategory<T extends { category: string; subcategory: string }>(
  parameters: T[]
): Map<string, Map<string, T[]>> {
  const categoryMap = new Map<string, Map<string, T[]>>();

  parameters.forEach((param) => {
    if (!categoryMap.has(param.category)) {
      categoryMap.set(param.category, new Map());
    }

    const subcategoryMap = categoryMap.get(param.category)!;
    if (!subcategoryMap.has(param.subcategory)) {
      subcategoryMap.set(param.subcategory, []);
    }

    subcategoryMap.get(param.subcategory)!.push(param);
  });

  return categoryMap;
}

export function transformToTreeStructure(
  parameters: ApiExternalDtoMeasurementParameterExternalDto[],
  t: (value: string) => string
): TreeListItemTsoPqProps[] {
  const categoryMap = groupParametersByCategory(parameters);

  return buildTreeFromCategoryMap(categoryMap, t, (params, category, subcategory, id) => ({
    id: `${id}`,
    entityId: 0,
    type: 'subcategory',
    label: subcategory === '' ? t('general.label.noSubcategoryFound') : t(subcategory),
    options: params.map((param) => ({
      id: param.technicalName,
      entityId: param.id ?? 0,
      type: 'parameter',
      label: `${t(param.name)} (${param.technicalName})`,
    })),
  }));
}

export function transformHarmonicsForTreeAutocomplete(
  parameters: ApiExternalDtoMeasurementParameterExternalDto[],
  t: (value: string) => string
): TreeListItemTsoPqProps[] {
  const categoryMap = groupParametersByCategory(parameters);

  return buildTreeFromCategoryMap(categoryMap, t, (params, category, subcategory) => ({
    id: `${subcategory.split('.').at(-1)}`,
    entityId: 0,
    type: 'subcategory',
    label: subcategory === '' ? 'no subcategory' : t(subcategory),
    subcategoryParameters: params.map((param) => param.id).filter((id) => typeof id === 'number'),
  }));
}
